type TRefinement = ReturnType<typeof import('widgets/search/Refinement').default>;
type TSizeTabs = ReturnType<typeof import('../global/CoreSizeTabs').default>;
type TTabPanel = ReturnType<typeof import('widgets/global/TabPanel').default>;
type TRefinementInstance = InstanceType<TRefinement>;

/**
 * @param RefinementMenuItem Base widget for extending
 * @returns CoreRefinementMenuItem widget
 */
export default function (RefinementMenuItem: ReturnType<typeof import('widgets/search/RefinementMenuItem').default>) {
    /**
     * @category widgets
     * @subcategory search
     * @class CoreRefinementMenuItem
     * @augments RefinementMenuItem
     * @classdesc Represents Refinement Menu Item with specific logic for submenus, keyboard navigation.
     * Represents RefinementMenuItem component with next features:
     * 1. Support keyboard navigation for accessibility
     * 2. Show control button according to selected refinements
     * 3. Build update url for refinement panel
     * 4. Allow apply/clear refinements
     * 5. Allow navigate thought submenus
     * 6. Using mustache template to render refinements
     *
     * RefinementMenuItem widget should be inside {@link RefinementsPanel} widgets that implement refinement panel.
     * RefinementMenuItem widget should contain {@link Refinement} widget inside.
     * @property {string} data-widget - Widget name `refinementMenuItem`
     * @property {string} data-event-keydown - Event listener for `handleKeydown` method
     * @property {boolean} [data-prevent-show-submenu=false] - prevent showing submenu
     * @property {boolean} [data-prevent-refinement-selected=false] - prevent showing submenu
     * @property {string} data-refinebar-url - refine bar url
     * @property {string} data-widget-event-previous - Previous panel handler
     * @property {string} data-update-url - update url
     * @example <caption>Example of RefinementMenuItem widget usage</caption>
     *
     * <div
     *     role="none"
     *     data-widget="refinementMenuItem"
     *     data-forward-to-parent="updateView:closePanel"
     *     data-refinement-selected="{{${'#'}selectedFiltersText}}true{{/selectedFiltersText}}{{^selectedFiltersText}}false{{/selectedFiltersText}}"
     *     data-title="{{displayName}}"
     *     data-refinement-id="{{refinementId}}"
     * >
     *     <div
     *         class="b-slide_panel-item"
     *         role="menuitem"
     *         aria-haspopup="true"
     *         aria-expanded="false"
     *         aria-owns="submenu-{{refinementId}}"
     *         tabindex="0"
     *         data-ref="itemLink"
     *         data-event-click.prevent="handleClick"
     *         data-tau="refinements_button"
     *     >
     *     <div class="b-slide_panel-link">
     *         ... link content
     *     </div>
     *     <span class="b-slide_panel-item_icon">
     *         <isinclude template="/common/icons/standalone/arrowForward" />
     *     </span>
     * </div>
     */
    class CoreRefinementMenuItem extends RefinementMenuItem {
        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();
            this.eventBus().on('refinement.menu.item.update.checked', 'updateCheckedStatusFoSizes');
        }

        /**
         * @description Get size refinement widgets
         * @param item Size Tabs widget
         * @returns Array of size refinement widgets
         */
        getSizeRefinements(item) {
            const Refinement = <TRefinement>this.getConstructor('refinement');
            const TabPanel = <TTabPanel>this.getConstructor('tabPanel');
            const sizeRefinements: Array<TRefinementInstance> = [];

            item.items.forEach((tabPanel: TTabPanel) => {
                if (tabPanel instanceof TabPanel) {
                    tabPanel.items.forEach(refinement => {
                        if (refinement instanceof Refinement && refinement.isAttributeRefinement()) {
                            refinement.value = refinement.value.replace('sizeName', '');
                            sizeRefinements.push(refinement);
                        }
                    });
                }
            });

            return sizeRefinements;
        }

        /**
         * @description Get refinement widgets
         * @returns Array of refinement widgets
         */
        getRefinements(): Array<TRefinementInstance> {
            const Refinement = <TRefinement>this.getConstructor('refinement');
            const SizeTabs = <TSizeTabs>this.getConstructor('sizeTabs');

            let refinements: Array<TRefinementInstance> = [];
            let sizeRefinements: Array<TRefinementInstance> = [];

            if (this.items) {
                refinements = this.items.filter(item => {
                    if (item instanceof Refinement) {
                        return item.isAttributeRefinement();
                    } else if (item instanceof SizeTabs) {
                        sizeRefinements = this.getSizeRefinements(item);
                    }

                    return false;
                }) as Array<TRefinementInstance>;
            }

            refinements = refinements.concat(sizeRefinements);

            return refinements;
        }

        updateCheckedStatusFoSizes(sizeRefinement) {
            this.getRefinements().forEach(refinement => {
                if (sizeRefinement.value.replace('sizeName', '') === refinement.value) {
                    if (sizeRefinement.selected) {
                        refinement.check();
                    } else {
                        refinement.uncheck();
                    }
                }
            });
        }
    }

    return CoreRefinementMenuItem;
}
